// Backend
import React from 'react';
import { createPortal } from 'react-dom';

// Frontend
import {
  PlusCircle,
} from 'react-bootstrap-icons';
import { FaInfoCircle } from 'react-icons/fa';
import { Modal, Button, Dropdown, DropdownButton } from 'react-bootstrap';
import { Grid, Cell } from 'react-foundation';
import '../../../stylesheets/components/conversations/Canvas.css';
import { Tooltip } from 'react-tooltip';

// Functionality
import { useLocation, Link } from 'react-router-dom';

// Components
import {
  FlashMessage,
  ProgressBar,
  ErrorRender
} from '../../../constants/SharedComponents';

import useCurrentUser from '../../../hooks/useCurrentUser';
import useWindowDimensions from '../../../hooks/useWindowDimensions';

// ReactFlow - core
import {
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  reconnectEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import Sidebar from '../../../components/ReactFlow/Sidebar';

// ReactFlow - nodes
import ImpactNode from '../../../components/ReactFlow/ImpactNode';
import CategoryNode from '../../../components/ReactFlow/CategoryNode';
import WhyNode from '../../../components/ReactFlow/WhyNode';
import { HashLink } from 'react-router-hash-link';
import ButtonEdge from '../../../components/ReactFlow/ButtonEdge';
import CustomLine from '../../../components/ReactFlow/ConnectionLine';

const initialNodes = [];

const nodeTypes = {
  impactNode: ImpactNode,
  categoryNode: CategoryNode,
  whyNode: WhyNode,
};

const edgeTypes = {
  buttonedge: ButtonEdge,
};

const defaultEdgeOptions = {
  label: 'Custom Edge',
  type: 'buttonedge'
}

let id = 0;
const defaultViewport = { x: 0, y: 0, zoom: 0.75 };
const getId = (nodeType) => `${nodeType}--${id++}`;
const snapGrid = [25, 25];

const onConnectStart = (_, { nodeId, handleType }) => {
};

const onConnectEnd = (event) => {
};

const nodeColor = (node) => {
  switch (node.type) {
    case 'impactNode':
      return '#E65722'; // Brand orange
    case 'categoryNode':
      return '#008A1E'; // Brand green
    case 'whyNode':
      return '#007acc'; // Blue-500
    default:
      return '#007acc'; 
  }
};

export function CanvasesCategoriesTest({ isDemo = false, submissionUnlocked: propsSubmissionUnlocked, demoNodes = null }) {
  // API
  const currentUser = useCurrentUser();
  const { state } = useLocation();

  // Objects
  const [inputs, setInputs] = React.useState({});
  const [error, setError] = React.useState(null);
  const [flashMessageObject, setFlashMessageObject] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [canvasData, setCanvasData] = React.useState({});
  const [submissionUnlocked, setSubmissionUnlocked] = React.useState(false);
  const [controlsCount, setControlsCount] = React.useState(1);
  const [actionsCount, setActionsCount] = React.useState({ 0: 1 });

  const [controlsAndActionsAreaClassName, setControlsAndActionsAreaClassName] =
    React.useState('hide');
  const [controlsAndActionsButtonClassName, setControlsAndActionsButtonClassName] =
    React.useState('');

  const [deleteControlModal, setDeleteControlModal] = React.useState(undefined);

  const { width } = useWindowDimensions();

  const widthThreshold = 1532;

  function handleOpenDeleteControlModal(e, id) {
    e.preventDefault();
    setDeleteControlModal(id);
  }
  function handleCloseDeleteControlModal(e) {
    e.preventDefault();
    setDeleteControlModal(undefined);
  }

  const [deleteActionModal, setDeleteActionModal] = React.useState(undefined);
  function handleOpenDeleteActionModal(e, id) {
    e.preventDefault();
    setDeleteActionModal(id);
  }
  function handleCloseDeleteActionModal(e) {
    e.preventDefault();
    setDeleteActionModal(undefined);
  }


  const controlsAndActionsAreaWrapper = React.useRef(null);
  const controlsAndActionsButtonWrapper = React.useRef(null);

  const [zoomToggle, setZoomToggle] = React.useState(true);

  // ReactFlow - core
  const reactFlowWrapper = React.useRef(null);
  const [nodes, setNodes] = useNodesState(demoNodes || initialNodes);
  const [edges, setEdges] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = React.useState();

  // ReactFlow - functionality
  const onNodesChange = React.useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes],
  );
  const onEdgesChange = React.useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges],
  );

  const onInit = React.useCallback((reactFlowInstance) => {
    setReactFlowInstance(reactFlowInstance);
  }, []);

  const onConnect = React.useCallback((params) => setEdges((eds) => addEdge(params, eds)), []);

  const onReconnect = React.useCallback(
    (oldEdge, newConnection) =>
      setEdges((els) => reconnectEdge(oldEdge, newConnection, els)),
    [],
  );

  const toggleControlsAndActions = React.useCallback(
    (event) => {
      const href = event.target.getAttribute('href');
      event.preventDefault();

      if (controlsAndActionsAreaClassName === 'hide') {
        setControlsAndActionsButtonClassName('hide');
        setControlsAndActionsAreaClassName('');
      } else {
        setControlsAndActionsButtonClassName('');
        setControlsAndActionsAreaClassName('hide');
      }

      if (href.includes('#')) {
        const id = `${href.substring(href.indexOf('#') + 1)}`;
        if (id) {
          setTimeout(
            function () {
              const anchor = document.getElementById(id);
              anchor.scrollIntoView({ behavior: 'smooth' });
            },
            250,
            id,
          );
        }
      }
    },
    [controlsAndActionsAreaClassName],
  );

  const addControl = React.useCallback(
    (event) => {
      event.preventDefault();
      let currentControlsCount = controlsCount + 1;

      const currentActionsCount = { ...actionsCount };
      currentActionsCount[controlsCount] = 1;

      setActionsCount(currentActionsCount);
      setControlsCount(currentControlsCount);
    },
    [controlsCount, actionsCount, inputs],
  );

  const addAction = React.useCallback(
    (event) => {
      event.preventDefault();

      const currentCount = { ...actionsCount };
      currentCount[event.target.getAttribute('data-controls-count')] =
        actionsCount[event.target.getAttribute('data-controls-count')] + 1;

      setActionsCount(currentCount);
    },
    [actionsCount],
  );

  const removeAction = React.useCallback(
    (event) => {
      event.preventDefault();

      const controlCount = event.target.getAttribute('data-controls-count');
      const actionCount = event.target.getAttribute('data-actions-count');

      let id = `canvas-category-actions--${controlCount}-${actionCount}`;

      document.getElementById(id).value = '';
      document.getElementById(id).parentElement.style = 'display:none';

      let newInputs = inputs;

      newInputs[id] = '';
      setDeleteActionModal(false);
      setInputs(newInputs);
    },
    [inputs],
  );

  const removeControl = React.useCallback(
    (event) => {
      event.preventDefault();

      const controlCount = event.target.getAttribute('data-controls-count');
      const actionCount = event.target.getAttribute('data-actions-count');

      let id = `canvas-category-controls--${controlCount}`;

      document.getElementById(id).value = '';
      document.getElementById(id).parentElement.parentElement.parentElement.parentElement.style =
        'display:none';

      let newInputs = inputs;

      newInputs[id] = '';

      let actionsIndex = 0;

      [...Array(actionCount)].forEach(_ => {
        let action = `canvas-category-actions--${controlCount}-${actionsIndex}`;

        let elem = document.getElementById(action);

        if (elem) {
          elem.value = '';
        }
        newInputs[action] = '';
        actionsIndex++;
      });
      setDeleteControlModal(false);
      setInputs(newInputs);
    },
    [inputs],
  );

  const onDragOver = React.useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = React.useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');

      // check if the dropped element is valid
      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = {
        id: getId(type),
        type,
        position,
        data: { label: `${type} node` },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance],
  );

  // @see https://github.com/wbkd/react-flow/blob/afa38727fad2400e817a3dcc6c575cb83cbf2bd2/examples/vite-app/src/examples/UseReactFlow/index.tsx
  const onNodeClick = React.useCallback(
    (event, element) => {
      if (
        event.target.className === 'js--delete-node conversation-canvas-node__button' &&
        submissionUnlocked
      ) {
        reactFlowInstance.deleteElements({
          nodes: [{ id: element.id }],
          edges: [],
        });
      }
    },
    [reactFlowInstance, submissionUnlocked],
  );

  // Functions
  const getData = async () => {
    setLoading(true);
    setError(false);
    getCanvasData()
    setLoading(false);
  };

  const getCanvasData = async () => {
    setLoading(true);

    let categoryTitle = `Example Topic`;

    let categoryValue = '';

    let flow;
    const defaultNodes = {
      nodes: [
        {
          id: 'categoryNode',
          type: 'categoryNode',
          deletable: false,
          position: { x: 0, y: 0 },
          data: {
            title: categoryTitle,
            value: categoryValue,
          },
        },
      ],
    };

    setSubmissionUnlocked(isDemo ? (propsSubmissionUnlocked !== undefined ? propsSubmissionUnlocked : false) : true);
    flow = {};
    flow[1] = defaultNodes;
    setCanvasData(flow);
    setNodes(flow[1].nodes);
    setLoading(false);
  };

  function isValidConnection(connection) {
    return connection.source !== connection.target;
  }

  function retryFetch() {
    setLoading(true);
    setError(false);
    getData();
  }

  const handleChange = (event) => {
    const name = event.target.name;
    let value = event.target.value;

    if (
      event.target.className.includes('canvas-category-controls-and-actions-section-item__input')
    ) {
      event.target.style.height = 0;
      event.target.style.height = `${event.target.scrollHeight + 5}px`;

      if (event.target.value === '') {
        value = ' '
      }
    }

    setInputs((values) => ({ ...values, [name]: value }));
  };

  const [isHover, setIsHover] = React.useState(false);

  const handleMouseEnter = () => {
    setIsHover(true);
  };
  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const handleEdgeMouseEnter = (e, edge) => {
    if (submissionUnlocked) {
      setEdges((edges) =>
        edges.map((edg) => {
          if (edg.id === edge.id) {
            return { ...edg, className: "change-edge", animated: true };
          } else {
            return edg;
          }
        })
      );
    }

    let element = document.getElementById(`${edge.id}-edgebutton`);

    if (element) {
      element.classList.remove('d-none');
    }
  };

  const handleEdgeMouseLeave = (e, edge) => {
    if (submissionUnlocked) {
      setEdges((edges) =>
        edges.map((edg) => {
          if (edg.id === edge.id) {
            return { ...edg, className: {}, animated: false };
          } else {
            return edg;
          }
        })
      );

      let element = document.getElementById(`${edge.id}-edgebutton`);

      if (element) {
        element.classList.add('d-none');
      }
    }
  };

  const handleScrollWheel = (event) => {
    const isCtrlPressed = event.ctrlKey;

    if (!isCtrlPressed) {
      event.stopPropagation();
      setZoomToggle(false);
    }
    else {
      setZoomToggle(true);
    }
  }

  React.useEffect(() => {
    if (isDemo && demoNodes) {
      return;
    }
    
    if (currentUser?.token) {
      retryFetch();
    }

    if (state?.hasOwnProperty('flash')) {
      setFlashMessageObject(state.flash);
    }
  }, [currentUser, state, isDemo, demoNodes]);

  // Update submissionUnlocked when props change in demo mode
  React.useEffect(() => {
    if (isDemo && propsSubmissionUnlocked !== undefined) {
      setSubmissionUnlocked(propsSubmissionUnlocked);
    }
  }, [isDemo, propsSubmissionUnlocked]);

  function setIndex(indexNumber) {
    return indexNumber;
  }

  return (
    <div className="canvas-content-wrap">
      <ProgressBar loading={loading} />
      <FlashMessage
        object={flashMessageObject}
        columnWrap={true}
        className="flash-message-compact"
      />
      <ErrorRender object={error} />

      <div className="page-content">
        <div className="page-content__header page-content__header--bordered page-content__header--fixed">
          <Grid className="grid-padding-x align-middle align-center" style={{ paddingBottom: '5px' }}>
            {width > widthThreshold && (
              <Cell large={3}>
                <Link
                  to="/pages/how-to-use-this-tool"
                  className="page-content__button--medium page-content__button--primary page-content__button--inline"
                  id="page-navigation-button--go-back"
                >
                  Back
                </Link>
                <Link
                  to="/resources"
                  className="page-content__button--medium page-content__button--secondary"
                  id="page-navigation-button--resources"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Resources
                </Link>
              </Cell>
            )}

            {width <= widthThreshold && (
              <Cell large={1}>
                <DropdownButton id="canvas-actions-dropdown" variant='canvas-actions' title="Conversation Actions">
                  <Dropdown.Item href="/pages/how-to-use-this-tool" className='canvas-actions'>
                    Back
                  </Dropdown.Item>
                  <Dropdown.Item href="/resources" className='canvas-actions-resources'>
                    Resources
                  </Dropdown.Item>
                </DropdownButton>
              </Cell>
            )}

            <Cell large={width > widthThreshold ? 6 : 10} className="text-center"></Cell>

            {width > widthThreshold && (
              <Cell large={3} className="text-right" />
            )}
            {width <= widthThreshold && (
              <Cell large={1} />
            )}
          </Grid>
        </div>

        {canvasData && (
          <div className="reactflow-wrap">
            <ReactFlowProvider>
              <div className="reactflow" ref={reactFlowWrapper}>
                <ReactFlow
                  nodes={nodes.map(node => {
                    const mappedNode = {
                      ...node,
                      data: {
                        ...node.data,
                        locked: !submissionUnlocked
                      }
                    };
                    return mappedNode;
                  })}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onReconnect={onReconnect}
                  onConnectStart={onConnectStart}
                  onConnectEnd={onConnectEnd}
                  onNodeClick={onNodeClick}
                  onInit={onInit}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  nodeTypes={nodeTypes}
                  snapToGrid={true}
                  snapGrid={snapGrid}
                  defaultViewport={defaultViewport}
                  nodesConnectable={submissionUnlocked}
                  edgesReconnectable={submissionUnlocked}
                  nodesDraggable={submissionUnlocked}
                  elementsSelectable={submissionUnlocked}
                  edgeTypes={edgeTypes}
                  defaultEdgeOptions={defaultEdgeOptions}
                  connectionLineComponent={CustomLine}
                  connectionMode="loose"
                  fitView
                  onEdgeMouseEnter={(e, edge) => handleEdgeMouseEnter(e, edge)}
                  onEdgeMouseLeave={(e, edge) => handleEdgeMouseLeave(e, edge)}
                  isValidConnection={isValidConnection}
                  zoomOnScroll={false}
                  panOnScroll={false}
                  onWheel={handleScrollWheel}
                  preventScrolling={zoomToggle}
                >
                  <Controls showInteractive={submissionUnlocked} />
                  <MiniMap
                    nodeColor={nodeColor}
                    nodeStrokeWidth={3}
                    zoomable
                    pannable
                  />
                </ReactFlow>
              </div>
              <Sidebar submissionUnlocked={submissionUnlocked} />
            </ReactFlowProvider>
          </div>
        )}
      </div>

      {submissionUnlocked && (
        <div
          className={`grid-container ${controlsAndActionsButtonClassName}`}
          ref={controlsAndActionsButtonWrapper}
        >
          <Grid className="grid-padding-x align-justify">
            <Cell small={12}>
              <div className="canvas-category-footer-wrap canvas-category-footer-wrap--top">
                <span className="canvas-category-footer-wrap__title">Next steps:</span>
                <HashLink
                  to="#canvas-controls-and-actions-form"
                  className="page-content__button"
                  onClick={toggleControlsAndActions}
                >
                  Add controls and actions for Example Topic
                </HashLink>
              </div>
            </Cell>
          </Grid>
        </div>
      )}
      <div
        className={`grid-container ${
          submissionUnlocked 
            ? controlsAndActionsAreaClassName 
            : controlsAndActionsAreaClassName === 'hide' 
              ? 'hide' 
              : ''
        }`}
        ref={controlsAndActionsAreaWrapper}
        id="canvas-controls-and-actions-form"
      >
        <Grid className="grid-padding-x">
          <Cell small={12}>
            <div className="canvas-category-controls-and-actions">
              <div className="canvas-category-controls-and-actions-header">
                <h2 className="canvas-category-controls-and-actions-header__title">
                  <strong className="canvas-category-controls-and-actions-header__emphasis canvas-category-controls-and-actions-header__emphasis--controls">
                    Controls{' '}
                    <span className="canvas-category-control-header-info-icon canvas-category-control-header-info-icon--white" title="Click for more information about Controls">
                      <FaInfoCircle size={16} />
                    </span>
                  </strong>{' '}
                  and{' '}
                  <strong className="canvas-category-controls-and-actions-header__emphasis canvas-category-controls-and-actions-header__emphasis--actions">
                    Actions{' '}
                    <span className="canvas-category-action-header-info-icon canvas-category-action-header-info-icon--white" title="Click for more information about Actions">
                      <FaInfoCircle size={16} />
                    </span>
                  </strong>{' '}
                  for Example Topic
                </h2>
              </div>
            </div>

            <form className="basic-form">
              {[...Array(controlsCount)].map((count, controlsIndex) => (
                <Grid
                  className="canvas-category-controls-and-actions-section-wrap"
                  key={`controls-count-${setIndex(controlsIndex)}`}
                >
                  {inputs['canvas-category-controls--' + controlsIndex] !== '' && (
                    <>
                      <Cell
                        small={12}
                        large={6}
                        className={width > 1024 ?
                          "canvas-category-controls-and-actions-section canvas-category-controls-and-actions-section--left" :
                          "canvas-category-controls-and-actions-section"}
                      >
                        <div className="canvas-category-controls-and-actions-section__inner">
                          <div className="canvas-category-controls-and-actions-section-item">
                            <div className="canvas-category-controls-and-actions-section-item__title canvas-category-controls-and-actions-section-item__title--control">
                              {submissionUnlocked && (
                                <>
                                  <span
                                    data-tooltip-id="control-tooltip"
                                    data-tooltip-place="top"
                                  >
                                    Control{' '}
                                    <span className="canvas-category-control-header-info-icon canvas-category-control-header-info-icon--white" title="Click for more information about Control">
                                      <FaInfoCircle size={16} />
                                    </span>
                                  </span>
                                  <button
                                    className="canvas-category-controls-and-actions-section-item__delete-button"
                                    id="canvas-category-controls-and-actions-section-item__delete-button"
                                    onClick={(e) => { handleOpenDeleteControlModal(e, controlsIndex) }}
                                    data-controls-count={controlsIndex}
                                    data-actions-count={actionsCount[controlsIndex]}
                                  >
                                    X
                                  </button>

                                  <Modal show={deleteControlModal === controlsIndex} style={{ paddingTop: "20px" }} id={controlsIndex} onHide={handleCloseDeleteControlModal}>
                                    {deleteControlModal === controlsIndex &&
                                      createPortal(
                                        <div className="modal fade show d-block" tabIndex="-1" aria-modal="true" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                          <div className="modal-dialog modal-dialog-centered">
                                            <div className="modal-content" style={{ borderRadius: 8, overflow: 'hidden' }}>
                                              <div style={{ position: 'relative', padding: '0 24px 0 24px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 56, borderBottom: '1px solid #e5e5e5', padding: '16px 0' }}>
                                                  <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 20 }}>
                                                    Delete Control
                                                  </div>
                                                  <button
                                                    type="button"
                                                    aria-label="Close"
                                                    onClick={handleCloseDeleteControlModal}
                                                    style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}
                                                  >
                                                    &times;
                                                  </button>
                                                </div>
                                                <div style={{ padding: '10px 0', textAlign: 'center' }}>
                                                  <p>Are you sure you want to delete this control?</p>
                                                </div>
                                                <div className="mb-3" style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                                                  <button
                                                    onClick={handleCloseDeleteControlModal}
                                                    className="page-content__button--medium page-content__button--secondary"
                                                    type="button"
                                                  >
                                                    Cancel
                                                  </button>
                                                  <button
                                                    onClick={removeControl}
                                                    className="page-content__button--medium page-content__button--danger"
                                                    onMouseEnter={handleMouseEnter}
                                                    onMouseLeave={handleMouseLeave}
                                                    data-controls-count={controlsIndex}
                                                    data-actions-count={actionsCount[controlsIndex]}
                                                    type="button"
                                                  >
                                                    Delete
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>,
                                        document.body
                                      )
                                    }
                                  </Modal>

                                </>
                              )}

                              {!submissionUnlocked && (
                                <>
                                  Controls{' '}
                                  <span className="canvas-category-control-header-info-icon canvas-category-control-header-info-icon--white" title="Click for more information about Control">
                                    <FaInfoCircle size={16} />
                                  </span>
                                </>
                              )}
                            </div>
                            {(submissionUnlocked || isDemo) && (
                              <textarea
                                className="canvas-category-controls-and-actions-section-item__input"
                                id={`canvas-category-controls--${controlsIndex}`}
                                name={`canvas-category-controls--${controlsIndex}`}
                                defaultValue={
                                  inputs[`canvas-category-controls--${controlsIndex}`] || ''
                                }
                                onChange={handleChange}
                                rows={5}
                                readOnly={!submissionUnlocked}
                                style={!submissionUnlocked ? {
                                  backgroundColor: '#e9ecef',
                                  color: '#212529',
                                  cursor: 'not-allowed'
                                } : {}}
                              ></textarea>
                            )}
                            {!submissionUnlocked && !isDemo && (
                              <div className="canvas-category-controls-and-actions-section-item__styles">
                                <p>
                                  {inputs[`canvas-category-controls--${controlsIndex}`]}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </Cell>
                      <Cell
                        className={width > 1024 ?
                          "canvas-category-controls-and-actions-section canvas-category-controls-and-actions-section--right" :
                          "canvas-category-controls-and-actions-section"}
                        small={12}
                        large={6}
                      >
                        <div className="canvas-category-controls-and-actions-section__inner">
                          <div className="canvas-category-controls-and-actions-section-item">
                            {(submissionUnlocked || isDemo) && (
                              <>
                                {[...Array(
                                  actionsCount[controlsIndex] > 0
                                    ? actionsCount[controlsIndex]
                                    : 1)].map((count, actionsIndex) => (
                                      <div
                                        key={`controls-count-${controlsIndex}--${actionsIndex}`}
                                      >
                                        {(inputs[
                                          'canvas-category-actions--' +
                                          controlsIndex +
                                          '-' +
                                          actionsIndex
                                        ] !== '' || (isDemo && !submissionUnlocked)) && (
                                            <>
                                              <div className="canvas-category-controls-and-actions-section-item__title canvas-category-controls-and-actions-section-item__title--action">
                                                {submissionUnlocked && (
                                                  <>
                                                    <span
                                                      data-tooltip-id="action-tooltip"
                                                      data-tooltip-place="top"
                                                    >
                                                      Action{' '}
                                                      <span className="canvas-category-action-header-info-icon canvas-category-action-header-info-icon--white" title="Click for more information about Action">
                                                        <FaInfoCircle size={16} />
                                                      </span>
                                                    </span>
                                                    <button
                                                      className="canvas-category-controls-and-actions-section-item__delete-button"
                                                      id="canvas-category-controls-and-actions-section-item__delete-button"
                                                      onClick={(e) => handleOpenDeleteActionModal(e, controlsIndex + "+" + actionsIndex)}
                                                      data-controls-count={controlsIndex}
                                                      data-actions-count={actionsIndex}
                                                      aria-label="Close delete action modal"
                                                      type="button"
                                                    >
                                                      X
                                                    </button>

                                                    {deleteActionModal === (controlsIndex + "+" + actionsIndex) &&
                                                      createPortal(
                                                        <div className="modal fade show d-block" tabIndex="-1" aria-modal="true" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                                          <div className="modal-dialog modal-dialog-centered">
                                                            <div className="modal-content" style={{ borderRadius: 8, overflow: 'hidden' }}>
                                                              <div style={{ position: 'relative', padding: '0 24px 0 24px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 56, borderBottom: '1px solid #e5e5e5', padding: '16px 0' }}>
                                                                  <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 20 }}>
                                                                    Delete Action
                                                                  </div>
                                                                  <button
                                                                    type="button"
                                                                    aria-label="Close"
                                                                    onClick={handleCloseDeleteActionModal}
                                                                    style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}
                                                                  >
                                                                    &times;
                                                                  </button>
                                                                </div>
                                                                <div style={{ padding: '10px 0', textAlign: 'center' }}>
                                                                  <p>Are you sure you want to delete this action?</p>
                                                                </div>
                                                                <div className="mb-3" style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                                                                  <button
                                                                    onClick={handleCloseDeleteActionModal}
                                                                    className="page-content__button--medium page-content__button--secondary"
                                                                    type="button"
                                                                  >
                                                                    Cancel
                                                                  </button>
                                                                  <button
                                                                    onClick={removeAction}
                                                                    className="page-content__button--medium page-content__button--danger"
                                                                    onMouseEnter={handleMouseEnter}
                                                                    onMouseLeave={handleMouseLeave}
                                                                    data-controls-count={controlsIndex}
                                                                    data-actions-count={actionsIndex}
                                                                    type="button"
                                                                  >
                                                                    Delete
                                                                  </button>
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </div>
                                                        </div>,
                                                        document.body
                                                      )
                                                    }
                                                  </>
                                                )}
                                                {!submissionUnlocked && <>Actions{' '}
                                                  <span className="canvas-category-action-header-info-icon canvas-category-action-header-info-icon--white" title="Click for more information about Action">
                                                    <FaInfoCircle size={16} />
                                                  </span>
                                                </>}
                                              </div>

                                              <textarea
                                                className="canvas-category-controls-and-actions-section-item__input canvas-category-controls-and-actions-section-item__input--action"
                                                id={`canvas-category-actions--${controlsIndex}-${actionsIndex}`}
                                                name={`canvas-category-actions--${controlsIndex}-${actionsIndex}`}
                                                onChange={handleChange}
                                                value={
                                                  inputs[
                                                  `canvas-category-actions--${controlsIndex}-${actionsIndex}`
                                                  ] || ''
                                                }
                                                rows={5}
                                                readOnly={!submissionUnlocked}
                                                style={!submissionUnlocked ? {
                                                  backgroundColor: '#e9ecef',
                                                  color: '#212529',
                                                  cursor: 'not-allowed'
                                                } : {}}
                                              ></textarea>
                                            </>
                                          )}
                                      </div>
                                    ))}
                                <div className="canvas-category-controls-and-actions-section-button-wrap">
                                  <button
                                    className="canvas-category-controls-and-actions-section-button canvas-category-controls-and-actions-section-button--action"
                                    id="canvas-category-controls-and-actions-section-add-button"
                                    onClick={addAction}
                                    data-controls-count={controlsIndex}
                                  >
                                    Add another action{''}
                                    <span className="canvas-category-controls-and-actions-section-button__icon">
                                      <PlusCircle size={16}
                                        data-controls-count={controlsIndex}
                                      />
                                    </span>
                                  </button>
                                </div>
                              </>
                            )}

                            {!submissionUnlocked && !isDemo && (
                              <>
                                <div className="canvas-category-controls-and-actions-section-item__title canvas-category-controls-and-actions-section-item__title--action">
                                  {!submissionUnlocked && (
                                    <>
                                      Actions{' '}
                                      <span className="canvas-category-action-header-info-icon canvas-category-action-header-info-icon--white" title="Click for more information about Action">
                                        <FaInfoCircle size={16} />
                                      </span>
                                    </>
                                  )}
                                </div>
                                {[...Array.apply(actionsCount[controlsIndex] > 0
                                  ? actionsCount[controlsIndex]
                                  : 1)].map((count, actionsIndex) => (
                                    <div
                                      className="canvas-category-controls-and-actions-section-item__styles"
                                      key={`controls-count-${controlsIndex}--${actionsIndex}`}
                                    >
                                      <p>
                                        {inputs[
                                          `canvas-category-actions--${controlsIndex}-${actionsIndex}`
                                        ] || ''}
                                      </p>
                                    </div>
                                  ))}
                              </>
                            )}
                          </div>
                        </div>
                      </Cell>
                    </>
                  )}
                </Grid>
              ))}

              <Tooltip id="control-tooltip" />
              <Tooltip id="action-tooltip" />

              {submissionUnlocked && (
                <Grid className="canvas-category-controls-and-actions-section-wrap text-right">
                  <Cell small={12}>
                    <div className="canvas-category-controls-and-actions-section-button-wrap">
                      <button
                        className="canvas-category-controls-and-actions-section-button canvas-category-controls-and-actions-section-button--control"
                        id="canvas-category-controls-and-actions-section-add-button"
                        onClick={addControl}
                      >
                        Add another control{''}
                        <span className="canvas-category-controls-and-actions-section-button__icon">
                          <PlusCircle size={16} />
                        </span>
                      </button>
                    </div>
                  </Cell>
                </Grid>
              )}
            </form>
          </Cell>
        </Grid>
      </div>
    </div>
  );
}
